/**
 * @reasoncode/client
 * Browser SDK for bot detection
 */

import { Batcher } from './batcher';
import { getSessionId, clearSession } from './session';
import { collectAllFeatures } from './collectors';
import { behavioralTracker } from './event-listeners/behavioral';
import { startMouseTracking } from './collectors/delbot';
import { getEndpoint, detectEnvironment } from './environments';
import type { DetectionSample } from '@reasoncode/types/detection';

export interface ReasonCodeConfig {
  projectId: string; // "prj_abc123"
  apiKey: string; // "rc_live_xyz"
  endpoint?: string; // Default: "https://ingest.reasoncode.ai"
  batchInterval?: number; // Default: 1500ms
  batchSize?: number; // Default: 20
  sessionTTL?: number; // Cookie TTL in seconds, default: 1800
  debug?: boolean; // Enable console logs
}

export interface ReasonCodeSDK {
  init(config: ReasonCodeConfig): void;
  destroy(): void;
  flush(): Promise<void>;
  getSessionId(): string;
  clearSession(): void;
}

class ReasonCodeSDKImpl implements ReasonCodeSDK {
  private batcher: Batcher | null = null;
  private config: ReasonCodeConfig | null = null;
  private intervalId: number | null = null;
  private unloadHandler: (() => void) | null = null;

  init(config: ReasonCodeConfig): void {
    if (this.batcher) {
      console.warn('[ReasonCode] Already initialized. Call destroy() first.');
      return;
    }

    if (typeof window === 'undefined') {
      console.error('[ReasonCode] SDK must be initialized in a browser environment');
      return;
    }

    this.config = {
      endpoint: 'https://reasoncode-ingest-api.fly.dev',
      batchInterval: 1500,
      batchSize: 20,
      sessionTTL: 1800,
      debug: false,
      ...config,
    };

    // Validate config
    if (!this.config.projectId || !this.config.apiKey) {
      console.error('[ReasonCode] projectId and apiKey are required');
      return;
    }

    // Initialize batcher
    this.batcher = new Batcher(
      this.config.endpoint!,
      this.config.projectId,
      this.config.apiKey,
      this.config.batchInterval!,
      this.config.batchSize!,
      this.config.debug!
    );

    // Start behavioral tracking
    behavioralTracker.start();

    // Start Delbot mouse tracking
    startMouseTracking();

    // Schedule periodic collection
    this.scheduleCollection();

    // Flush on page unload
    this.unloadHandler = () => this.batcher?.flushSync();
    window.addEventListener('beforeunload', this.unloadHandler);

    if (this.config.debug) {
      console.log('[ReasonCode] Initialized', {
        projectId: this.config.projectId,
        sessionId: getSessionId(this.config.sessionTTL),
        endpoint: this.config.endpoint,
      });
    }
  }

  destroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.unloadHandler) {
      window.removeEventListener('beforeunload', this.unloadHandler);
      this.unloadHandler = null;
    }

    const debug = this.config?.debug;
    
    behavioralTracker.stop();
    this.batcher?.flushSync();
    this.batcher = null;
    this.config = null;

    if (debug) {
      console.log('[ReasonCode] Destroyed');
    }
  }

  async flush(): Promise<void> {
    await this.batcher?.flush();
  }

  getSessionId(): string {
    return getSessionId(this.config?.sessionTTL);
  }

  clearSession(): void {
    clearSession();
  }

  private scheduleCollection(): void {
    if (typeof window === 'undefined') return;

    this.intervalId = window.setInterval(() => {
      this.collectAndBatch();
    }, this.config!.batchInterval);

    // Also collect immediately
    this.collectAndBatch();
  }

  private async collectAndBatch(): Promise<void> {
    if (!this.batcher || !this.config) return;

    try {
      const features = await collectAllFeatures();
      const behavioral = behavioralTracker.getData();

      // Map webApi features to match DetectionSample type
      const webApi: DetectionSample['webApi'] = {};
      
      if (features.webApi.battery && 
          features.webApi.battery.charging !== undefined &&
          features.webApi.battery.level !== undefined &&
          features.webApi.battery.chargingTime !== undefined &&
          features.webApi.battery.dischargingTime !== undefined) {
        webApi.battery = {
          charging: features.webApi.battery.charging,
          level: features.webApi.battery.level,
          chargingTime: features.webApi.battery.chargingTime,
          dischargingTime: features.webApi.battery.dischargingTime
        };
      }

      if (features.webApi.bluetooth?.available !== undefined) {
        webApi.bluetooth = {
          available: features.webApi.bluetooth.available
        };
      }

      if (features.webApi.computePressure?.supported !== undefined) {
        webApi.computePressure = {
          supported: features.webApi.computePressure.supported
        };
      }

      if (features.webApi.inputDevice) {
        webApi.inputDevice = {
          pointerEvents: features.webApi.inputDevice.pointerEvents || 0,
          maxTouchPoints: features.webApi.inputDevice.maxTouchPoints || 0
        };
      }

      if (features.webApi.mediaCapabilities?.supported !== undefined) {
        webApi.mediaCapabilities = {
          supported: features.webApi.mediaCapabilities.supported
        };
      }

      if (features.webApi.network) {
        webApi.network = {
          type: features.webApi.network.type || 'unknown',
          effectiveType: features.webApi.network.effectiveType || 'unknown',
          downlink: features.webApi.network.downlink || 0,
          rtt: features.webApi.network.rtt || 0
        };
      }

      if (features.webApi.permissions) {
        webApi.permissions = {
          queriedCount: features.webApi.permissions.queriedCount
        };
      }

      if (features.webApi.wakeLock?.supported !== undefined) {
        webApi.wakeLock = {
          supported: features.webApi.wakeLock.supported
        };
      }

      if (features.webApi.sensors) {
        webApi.sensors = {
          accelerometer: features.webApi.sensors.accelerometer || false,
          gyroscope: features.webApi.sensors.gyroscope || false,
          magnetometer: features.webApi.sensors.magnetometer || false
        };
      }

      if (features.webApi.userAgentHints) {
        webApi.userAgentHints = {
          brands: features.webApi.userAgentHints.brands || [],
          mobile: features.webApi.userAgentHints.mobile || false,
          platform: features.webApi.userAgentHints.platform || 'unknown'
        };
      }

      if (features.webApi.webgpu?.available !== undefined) {
        webApi.webgpu = {
          available: features.webApi.webgpu.available
        };
      }

      if (features.webApi.webrtc) {
        webApi.webrtc = {
          supported: features.webApi.webrtc.supported,
          devices: features.webApi.webrtc.devices
        };
      }

      const sample: DetectionSample = {
        timestamp: Date.now(),
        webApi,
        delbot: features.delbot,
        behavioral,
        meta: {
          userAgent: navigator.userAgent,
          referrer: document.referrer,
          pageUrl: window.location.href,
        },
      };

      this.batcher.add(sample);

      // Reset behavioral counters after collection
      behavioralTracker.reset();

      if (this.config.debug) {
        console.log('[ReasonCode] Collected sample', sample);
      }
    } catch (err) {
      if (this.config.debug) {
        console.error('[ReasonCode] Collection failed:', err);
      }
    }
  }
}

// Singleton instance
const sdkInstance = new ReasonCodeSDKImpl();

// Export as global object
export const ReasonCode: ReasonCodeSDK = sdkInstance;

// Auto-attach to window for UMD builds
if (typeof window !== 'undefined') {
  (window as Window & { ReasonCode?: ReasonCodeSDK }).ReasonCode = ReasonCode;
}

// Export utilities
export { getSessionId, clearSession };
